import { Component, OnInit, OnDestroy } from '@angular/core';

import { CompanyService } from '@s-services/company.service';
import { CalendarService } from '@s-services/calendar.service';
import { MatDialog } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DialogContainerComponent } from '@s-containers/dialog-container/dialog-container.component';
import { BottomsheetContainerComponent } from '@s-containers/bottomsheet-container/bottomsheet-container.component';
import { CommonModule } from '@angular/common';

import { ListBookingGroupsComponent } from '@s-components/list-booking-groups/list-booking-groups.component';
import { ListBookingProfilesComponent } from '@s-components/list-booking-profiles/list-booking-profiles.component';
import { ListResourcesAsBookingprofilesComponent } from '@s-components/list-resources-as-bookingprofiles/list-resources-as-bookingprofiles.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { LucideAngularModule, ChevronLeft, ChevronRight, ChevronDown, EllipsisVertical, Minus, Plus, Settings } from 'lucide-angular';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmSeparator } from '@spartan-ng/helm/separator';
import { HlmCalendar } from '@spartan-ng/helm/calendar';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { RouterModule } from '@angular/router';

@Component({
    selector: 'app-calendar-header',
    templateUrl: './calendar-header.component.html',
    styleUrls: ['./calendar-header.component.scss'],
    imports: [
        CommonModule,
        LucideAngularModule,
        ListBookingGroupsComponent,
        ListBookingProfilesComponent,
        ListResourcesAsBookingprofilesComponent,
        HlmButton,
        ...HlmDropdownMenuImports,
        HlmSeparator,
        HlmCalendar,
        ...HlmPopoverImports,
        RouterModule,
    ]
})
export class CalendarHeaderComponent implements OnInit, OnDestroy {
  readonly ChevronLeft = ChevronLeft;
  readonly ChevronRight = ChevronRight;
  readonly ChevronDown = ChevronDown;
  readonly EllipsisVertical = EllipsisVertical;
  readonly Minus = Minus;
  readonly Plus = Plus;
  readonly Settings = Settings;
  private destroy$ = new Subject<void>();
  calendarZoomLabel: number | undefined;

  constructor(
    public companyService: CompanyService,
    public calendarService: CalendarService,
    private dialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private breakpointObserver: BreakpointObserver
  ) { }

  ngOnInit() {
    this.calendarService.slotDuration.pipe(takeUntil(this.destroy$)).subscribe(slotDuration => {
      this.calendarZoomLabel = [5, 10, 15, 20, 30][slotDuration.step];
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get viewLabel() {
    return {
      resourceTimeGridWeek: 'Woche (Mo-So)',
      resourceTimeGridWorkweek: 'Woche (Mo-Fr)',
      resourceTimeGridDay: 'Tag'
    };
  }

  onDateSelected(date: Date) {
    this.calendarService.calendarNav.next(date);
  }

  onUpdateCalendarSize(e: string) {
    let duration, updatedStep;

    if (e === 'minus' && this.calendarService.getStorageCalendarSetting('slotDuration').step !== 0) {
      updatedStep = this.calendarService.getStorageCalendarSetting('slotDuration').step - 1;
      duration = ['00:05', '00:10', '00:15', '00:20', '00:30'][updatedStep];
      this.calendarService.updateStorageCalendarSettings('slotDuration', { step: updatedStep, duration: duration });
    } else if (e === 'plus' && this.calendarService.getStorageCalendarSetting('slotDuration').step !== 4) {
      updatedStep = this.calendarService.getStorageCalendarSetting('slotDuration').step + 1;
      duration = ['00:05', '00:10', '00:15', '00:20', '00:30'][updatedStep];
      this.calendarService.updateStorageCalendarSettings('slotDuration', { step: updatedStep, duration: duration });
    }

    if (duration && updatedStep !== undefined) {
      this.calendarService.slotDuration.next({ step: updatedStep, duration: duration });
    }
  }

  onToggleIgnoreRulesForQuickEdit() {
    const curr = this.calendarService.getStorageCalendarSetting('ignoreRulesForQuickEdit');
    this.calendarService.updateStorageCalendarSettings('ignoreRulesForQuickEdit', !curr);
  }

  onToggleUpdateBetweenResources(e: boolean) {
    this.calendarService.updateStorageCalendarSettings('letEventDragBetweenResources', e);
  }

  openUpdateBookingTimeDialog() {
    this.breakpointObserver.observe([Breakpoints.Small, Breakpoints.XSmall])
    .subscribe(result => {
      if (result.matches) {
        const sheetRef = this.bottomSheet.open(BottomsheetContainerComponent, {
          data: {
            component: 'HoursExceptionComponent'
          }
        });
      } else {
        const dialogRef = this.dialog.open(DialogContainerComponent, {
          data: {
            component: 'HoursExceptionComponent'
          },
          width: '500px',
          autoFocus: false,
          hasBackdrop: true,
          panelClass: 'smly-dialog-no-padding',
        });
      }
    });
  }

  openCalendarSettingsDialog() {
    this.breakpointObserver.observe([Breakpoints.Small, Breakpoints.XSmall])
    .subscribe(result => {
      if (result.matches) {
        const sheetRef = this.bottomSheet.open(BottomsheetContainerComponent, {
          data: {
            component: 'CalendarSettingsComponent'
          }
        });
      } else {
        const dialogRef = this.dialog.open(DialogContainerComponent, {
          data: {
            component: 'CalendarSettingsComponent'
          },
          width: '500px',
          autoFocus: false,
          hasBackdrop: true,
          panelClass: 'smly-dialog-no-padding',
        });
      }
    });
  }

  openReorderDialog() {
    this.breakpointObserver.observe([Breakpoints.Small, Breakpoints.XSmall])
    .subscribe(result => {
      if (result.matches) {
        const sheetRef = this.bottomSheet.open(BottomsheetContainerComponent, {
          data: {
            component: 'CalendarReorderComponent'
          }
        });
      } else {
        const dialogRef = this.dialog.open(DialogContainerComponent, {
          data: {
            component: 'CalendarReorderComponent'
          },
          width: '400px',
          autoFocus: false,
          hasBackdrop: true,
          panelClass: 'smly-dialog-no-padding',
        });
      }
    });
  }
}
